import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Page from "../components/Page";
import {
  IconCalendar,
  IconCheck,
  IconAlertTriangle,
  IconPrinter,
  IconDownload,
  IconScale,
  IconBuildingBank,
  IconCalculator,
  IconFileText,
} from "@tabler/icons-react";
import { useTrialBalance } from "../controllers/reports.controller";
import { useStoreSettings } from "../controllers/settings.controller";
import { useTheme } from "../contexts/ThemeContext";

export default function TrialBalanceReportPage() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { data: storeSettings } = useStoreSettings();
  const store = {
    name: storeSettings?.storeName || storeSettings?.name || "Restaurant & Hotel",
    address: storeSettings?.address,
    phone: storeSettings?.phone,
    panNo: storeSettings?.panNo,
    currency: storeSettings?.currency || "NPR"
  };

  const now = new Date();
  const firstDay = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().substring(0, 10);
  const today = now.toISOString().substring(0, 10);

  const [dateFrom, setDateFrom] = useState(firstDay);
  const [dateTo, setDateTo] = useState(today);

  const { data, isLoading, error } = useTrialBalance({ from: dateFrom, to: dateTo });

  const handlePrint = () => {
    window.print();
  };

  const handleExportCSV = async () => {
    if (!data?.rows) return;
    try {
      const { Parser } = await import("@json2csv/plainjs");
      const { saveAs } = await import("file-saver");

      const exportData = data.rows.map(r => ({
        "Account": r.account,
        "Type": r.type,
        "Sub-Type": r.subType,
        "Opening Debit": r.openingDebit.toFixed(2),
        "Opening Credit": r.openingCredit.toFixed(2),
        "Period Debit": r.periodDebit.toFixed(2),
        "Period Credit": r.periodCredit.toFixed(2),
        "Closing Debit": r.closingDebit.toFixed(2),
        "Closing Credit": r.closingCredit.toFixed(2),
      }));

      // Add Grand Totals
      exportData.push({
        "Account": "TOTALS",
        "Type": "",
        "Sub-Type": "",
        "Opening Debit": data.totals.totalOpeningDebit.toFixed(2),
        "Opening Credit": data.totals.totalOpeningCredit.toFixed(2),
        "Period Debit": data.totals.totalPeriodDebit.toFixed(2),
        "Period Credit": data.totals.totalPeriodCredit.toFixed(2),
        "Closing Debit": data.totals.totalClosingDebit.toFixed(2),
        "Closing Credit": data.totals.totalClosingCredit.toFixed(2),
      });

      const parser = new Parser();
      const csv = parser.parse(exportData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, `Trial_Balance_${dateFrom}_to_${dateTo}.csv`);
    } catch (err) {
      console.error(err);
    }
  };

  if (isLoading) {
    return (
      <Page>
        <div className="flex items-center justify-center h-96">
          <span className="loading loading-spinner loading-lg text-restro-green"></span>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page>
        <div className="p-8 text-center text-red-500 font-bold">
          Failed to load Trial Balance: {error.message}
        </div>
      </Page>
    );
  }

  const { rows = [], totals = {} } = data || {};

  // Group rows by account type
  const groupedAccounts = {
    Asset: rows.filter(r => r.type === "Asset"),
    Liability: rows.filter(r => r.type === "Liability"),
    Equity: rows.filter(r => r.type === "Equity"),
    Revenue: rows.filter(r => r.type === "Revenue"),
    Expense: rows.filter(r => r.type === "Expense"),
  };

  return (
    <Page>
      <div className="max-w-7xl mx-auto space-y-6 pb-12">
        {/* Accounting Reports Suite Navigation */}
        <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 dark:border-gray-800 pb-4 print:hidden">
          <Link
            to="/dashboard/reports/profit-loss"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconCalculator size={17} />
            Profit & Loss Audit
          </Link>
          <Link
            to="/dashboard/reports/trial-balance"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-restro-green text-white shadow-md shadow-green-100 dark:shadow-none"
          >
            <IconScale size={17} />
            Trial Balance
          </Link>
          <Link
            to="/dashboard/reports/balance-sheet"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconBuildingBank size={17} />
            Balance Sheet
          </Link>
          <Link
            to="/dashboard/reports/ledger"
            className="flex items-center gap-1.5 px-4 py-2 rounded-2xl font-bold text-xs md:text-sm bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition"
          >
            <IconFileText size={17} />
            General Ledger
          </Link>
        </div>

        {/* Header and Controls */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden">
          <div>
            <h1 className="text-2xl md:text-3xl font-black text-restro-text dark:text-white flex items-center gap-2">
              <IconScale size={28} className="text-restro-green" />
              Trial Balance Statement
            </h1>
            <p className="text-xs md:text-sm text-gray-500">
              Authoritative double-entry ledger balance summary across all accounts
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-800 p-1 rounded-2xl border border-restro-border-green text-xs font-bold">
              {[
                { id: "today", label: "Today", getRange: () => [today, today] },
                { id: "this_month", label: "This Month", getRange: () => [firstDay, today] },
                { id: "this_year", label: "This Year", getRange: () => [`${now.getFullYear()}-01-01`, today] },
                { id: "all_time", label: "All Time", getRange: () => ["2020-01-01", today] },
              ].map((p) => (
                <button
                  key={p.id}
                  onClick={() => {
                    const [f, t] = p.getRange();
                    setDateFrom(f);
                    setDateTo(t);
                  }}
                  className={`px-3 py-1.5 rounded-xl transition ${
                    dateFrom === p.getRange()[0] && dateTo === p.getRange()[1]
                      ? "bg-restro-green text-white shadow-sm"
                      : "text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2 bg-white dark:bg-gray-800 border border-restro-border-green px-3 py-2 rounded-2xl shadow-sm text-sm">
              <IconCalendar size={18} className="text-gray-400" />
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="bg-transparent font-bold outline-none text-xs md:text-sm"
              />
              <span className="text-gray-400 font-medium">to</span>
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="bg-transparent font-bold outline-none text-xs md:text-sm"
              />
            </div>

            <button
              onClick={handlePrint}
              className="flex items-center gap-2 px-4 py-2 bg-restro-gray hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-restro-text rounded-2xl font-bold text-sm transition active:scale-95"
            >
              <IconPrinter size={18} />
              Print
            </button>

            <button
              onClick={handleExportCSV}
              className="flex items-center gap-2 px-4 py-2 bg-restro-green hover:bg-restro-green-button-hover text-white rounded-2xl font-bold text-sm transition active:scale-95 shadow-md shadow-green-100 dark:shadow-none"
            >
              <IconDownload size={18} />
              Export CSV
            </button>
          </div>
        </div>

        {/* Printable White Paper Report Container */}
        <div id="printable-report" className="printable-report space-y-6">
          {/* Professional White Paper Print Letterhead (Print Only) */}
          <div className="hidden print:block text-center pb-4 mb-6 border-b-2 border-black">
            <h1 className="text-2xl font-black uppercase tracking-wider text-black">
              {store.name}
            </h1>
            <p className="text-xs text-gray-700 mt-0.5">
              {[store.address, store.phone && `Phone: ${store.phone}`, store.panNo && `PAN/VAT: ${store.panNo}`].filter(Boolean).join(" • ")}
            </p>
            <div className="mt-3 pt-2 border-t border-gray-400 flex justify-between items-center text-[10pt] font-bold text-black">
              <span className="uppercase tracking-wide font-black">
                TRIAL BALANCE STATEMENT
              </span>
              <span className="text-xs font-normal">
                Period: <strong>{dateFrom} to {dateTo}</strong>
              </span>
            </div>
            <div className="flex justify-between text-[9pt] text-gray-600 mt-1">
              <span>Printed On: {new Date().toLocaleString()}</span>
              <span>Currency: {store.currency}</span>
            </div>
          </div>

        {/* Balance Status Banner */}
        <div
          className={`p-4 rounded-3xl border flex items-center justify-between ${
            totals.isBalanced
              ? "bg-emerald-50 dark:bg-emerald-950/20 border-emerald-300 dark:border-emerald-800 text-emerald-800 dark:text-emerald-300"
              : "bg-rose-50 dark:bg-rose-950/20 border-rose-300 dark:border-rose-800 text-rose-800 dark:text-rose-300"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`w-10 h-10 rounded-2xl flex items-center justify-center font-bold text-white ${
                totals.isBalanced ? "bg-emerald-600" : "bg-rose-600"
              }`}
            >
              {totals.isBalanced ? <IconCheck size={22} /> : <IconAlertTriangle size={22} />}
            </div>
            <div>
              <h4 className="font-black text-sm md:text-base">
                {totals.isBalanced ? "Trial Balance is Perfectly Balanced" : "Trial Balance is Out of Balance"}
              </h4>
              <p className="text-xs opacity-80">
                {totals.isBalanced
                  ? "Total debits match total credits according to standard double-entry principles."
                  : `Discrepancy detected: Difference of ${(totals.difference || 0).toFixed(2)} between Debits and Credits.`}
              </p>
            </div>
          </div>

          <div className="text-right hidden sm:block">
            <div className="text-xs uppercase tracking-widest font-black opacity-70">Closing Balance</div>
            <div className="text-lg font-black font-mono">
              {(totals.totalClosingDebit || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 print:hidden">
          <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl border border-restro-border-green shadow-sm">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Period Debits</span>
            <div className="text-xl md:text-2xl font-black text-blue-600 font-mono mt-1">
              {(totals.totalPeriodDebit || 0).toFixed(2)}
            </div>
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl border border-restro-border-green shadow-sm">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Period Credits</span>
            <div className="text-xl md:text-2xl font-black text-purple-600 font-mono mt-1">
              {(totals.totalPeriodCredit || 0).toFixed(2)}
            </div>
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl border border-restro-border-green shadow-sm">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Total Closing Debit</span>
            <div className="text-xl md:text-2xl font-black text-emerald-600 font-mono mt-1">
              {(totals.totalClosingDebit || 0).toFixed(2)}
            </div>
          </div>

          <div className="p-4 bg-white dark:bg-gray-800 rounded-3xl border border-restro-border-green shadow-sm">
            <span className="text-xs text-gray-400 font-bold uppercase tracking-wider">Total Closing Credit</span>
            <div className="text-xl md:text-2xl font-black text-emerald-600 font-mono mt-1">
              {(totals.totalClosingCredit || 0).toFixed(2)}
            </div>
          </div>
        </div>

        {/* Trial Balance Table */}
        <div className="bg-white dark:bg-gray-900 border border-restro-border-green rounded-3xl overflow-hidden shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs md:text-sm">
              <thead>
                <tr className="bg-restro-gray dark:bg-gray-800 text-gray-600 dark:text-gray-300 font-bold uppercase tracking-wider border-b border-restro-border-green text-[11px]">
                  <th className="py-3.5 px-4" rowSpan={2}>Account Name</th>
                  <th className="py-3.5 px-3" rowSpan={2}>Type / Classification</th>
                  <th className="py-2 px-3 text-center border-b border-restro-border-green" colSpan={2}>Opening Balance</th>
                  <th className="py-2 px-3 text-center border-b border-restro-border-green" colSpan={2}>Period Movement</th>
                  <th className="py-2 px-4 text-center border-b border-restro-border-green" colSpan={2}>Closing Balance</th>
                </tr>
                <tr className="bg-gray-50 dark:bg-gray-800/60 text-gray-500 font-semibold text-[10px] uppercase border-b border-restro-border-green">
                  <th className="py-2 px-3 text-right">Debit</th>
                  <th className="py-2 px-3 text-right">Credit</th>
                  <th className="py-2 px-3 text-right">Debit</th>
                  <th className="py-2 px-3 text-right">Credit</th>
                  <th className="py-2 px-4 text-right bg-emerald-50/40 dark:bg-emerald-950/10">Debit</th>
                  <th className="py-2 px-4 text-right bg-emerald-50/40 dark:bg-emerald-950/10">Credit</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 font-medium">
                {Object.entries(groupedAccounts).map(([type, accList]) => {
                  if (accList.length === 0) return null;

                  const subClosingDebit = accList.reduce((sum, r) => sum + r.closingDebit, 0);
                  const subClosingCredit = accList.reduce((sum, r) => sum + r.closingCredit, 0);

                  return (
                    <React.Fragment key={type}>
                      {/* Section Header */}
                      <tr className="bg-gray-100/70 dark:bg-gray-800 font-black text-xs text-gray-700 dark:text-gray-200">
                        <td colSpan={8} className="py-2.5 px-4 uppercase tracking-wider">
                          {type} Accounts ({accList.length})
                        </td>
                      </tr>

                      {/* Account Rows */}
                      {accList.map((row, idx) => (
                        <tr
                          key={idx}
                          className="hover:bg-gray-50/60 dark:hover:bg-gray-800/40 transition-colors"
                        >
                          <td className="py-2.5 px-4 font-bold text-restro-text dark:text-gray-100">
                            {row.account}
                          </td>
                          <td className="py-2.5 px-3 text-gray-500 text-xs">
                            {row.subType || row.type}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-400">
                            {row.openingDebit > 0 ? row.openingDebit.toFixed(2) : "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-gray-600 dark:text-gray-400">
                            {row.openingCredit > 0 ? row.openingCredit.toFixed(2) : "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-blue-600 dark:text-blue-400">
                            {row.periodDebit > 0 ? row.periodDebit.toFixed(2) : "-"}
                          </td>
                          <td className="py-2.5 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                            {row.periodCredit > 0 ? row.periodCredit.toFixed(2) : "-"}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-600 bg-emerald-50/30 dark:bg-emerald-950/10">
                            {row.closingDebit > 0 ? row.closingDebit.toFixed(2) : "-"}
                          </td>
                          <td className="py-2.5 px-4 text-right font-mono font-bold text-emerald-600 bg-emerald-50/30 dark:bg-emerald-950/10">
                            {row.closingCredit > 0 ? row.closingCredit.toFixed(2) : "-"}
                          </td>
                        </tr>
                      ))}

                      {/* Subtotal Row */}
                      <tr className="bg-gray-50/50 dark:bg-gray-800/30 font-bold text-xs text-gray-500 border-b">
                        <td colSpan={6} className="py-2 px-4 text-right">
                          Subtotal {type}:
                        </td>
                        <td className="py-2 px-4 text-right font-mono font-bold text-gray-700 dark:text-gray-300">
                          {subClosingDebit.toFixed(2)}
                        </td>
                        <td className="py-2 px-4 text-right font-mono font-bold text-gray-700 dark:text-gray-300">
                          {subClosingCredit.toFixed(2)}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}

                {rows.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-400">
                      No journal entries found for the selected time range.
                    </td>
                  </tr>
                )}
              </tbody>

              {/* Grand Totals Footer */}
              <tfoot>
                <tr className="bg-restro-gray dark:bg-gray-800 font-black text-sm border-t-2 border-restro-border-green">
                  <td colSpan={2} className="py-4 px-4 uppercase tracking-widest text-restro-text dark:text-white">
                    Grand Totals
                  </td>
                  <td className="py-4 px-3 text-right font-mono">
                    {(totals.totalOpeningDebit || 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-right font-mono">
                    {(totals.totalOpeningCredit || 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-right font-mono text-blue-600 dark:text-blue-400">
                    {(totals.totalPeriodDebit || 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-3 text-right font-mono text-purple-600 dark:text-purple-400">
                    {(totals.totalPeriodCredit || 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-4 text-right font-mono text-emerald-600 font-black text-base bg-emerald-100/50 dark:bg-emerald-950/30">
                    {(totals.totalClosingDebit || 0).toFixed(2)}
                  </td>
                  <td className="py-4 px-4 text-right font-mono text-emerald-600 font-black text-base bg-emerald-100/50 dark:bg-emerald-950/30">
                    {(totals.totalClosingCredit || 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Formal Three-Column Signatures (Prepared By, Verified By, Approved By) */}
        <div className="signature-section mt-16 pt-8 border-t-2 border-black avoid-break">
          <div className="grid grid-cols-3 gap-6 text-center">
            {/* Prepared By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Prepared By</p>
              <p className="text-[11px] text-gray-500">Accountant / Cashier</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Verified By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Verified By</p>
              <p className="text-[11px] text-gray-500">Internal Auditor / Finance</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>

            {/* Approved By */}
            <div className="flex flex-col items-center">
              <div className="w-44 border-b-2 border-black mb-2"></div>
              <p className="font-bold text-xs uppercase tracking-wider text-black dark:text-gray-200">Approved By</p>
              <p className="text-[11px] text-gray-500">Managing Director / Owner</p>
              <p className="text-[10px] text-gray-400 mt-2">Date: ________________</p>
            </div>
          </div>
        </div>
      </div>
    </div>

      {/* Professional White Paper Print CSS */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 12mm 15mm;
          }
          html, body {
            background: #ffffff !important;
            color: #000000 !important;
            font-size: 10pt !important;
            margin: 0 !important;
            padding: 0 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          nav, aside, header, .appbar, .navbar, .print\\:hidden, button, input, select, a {
            display: none !important;
          }
          div[class*="pl-"], div[class*="md:pl-"], [class*="pl-"] {
            padding-left: 0 !important;
            margin-left: 0 !important;
          }
          body * {
            visibility: hidden;
          }
          #printable-report, #printable-report * {
            visibility: visible;
          }
          #printable-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            background: #ffffff !important;
            color: #000000 !important;
            padding: 0 !important;
            margin: 0 !important;
            border: none !important;
            box-shadow: none !important;
          }
          #printable-report table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8.5pt !important;
            color: #000000 !important;
            background: #ffffff !important;
          }
          #printable-report th {
            background-color: #f3f4f6 !important;
            color: #000000 !important;
            border: 1px solid #9ca3af !important;
            padding: 5px 6px !important;
            font-weight: bold !important;
            text-transform: uppercase !important;
          }
          #printable-report td {
            border: 1px solid #d1d5db !important;
            padding: 4px 6px !important;
            color: #000000 !important;
            background: transparent !important;
          }
          #printable-report tfoot td {
            border-top: 2px solid #000000 !important;
            border-bottom: 3px double #000000 !important;
            font-weight: 800 !important;
            background-color: #f9fafb !important;
          }
          tr, .avoid-break {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
          }
          .signature-section {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            margin-top: 40px !important;
            padding-top: 15px !important;
          }
        }
      `}</style>
    </Page>
  );
}
